import Axios, {
	type AxiosError,
	type AxiosRequestConfig,
	type InternalAxiosRequestConfig,
} from "axios";

import { HEADER_SESSION_ID, getChatbotSessionKey } from "@virtual-brain-lib/utils-common";

import env from "../environments";

export const CHATBOT_API_AXIOS_INSTANCE = Axios.create({
	baseURL: env.chatbotApiUrl,
	withCredentials: true,
});

let chatbotApiAccessToken: string | null = null;

export function setChatbotApiAccessToken(token: string) {
	chatbotApiAccessToken = token;
}

CHATBOT_API_AXIOS_INSTANCE.interceptors.request.use((config) => {
	if (chatbotApiAccessToken) {
		config.headers.Authorization = `Bearer ${chatbotApiAccessToken}`;
	}

	return config;
});

interface SetupChatbotApiInterceptorsParams {
	getChatbotId: () => string;
	isPreview?: boolean;
	onAuthError: (chatbotId: string) => Promise<{ accessToken: string }>;
}

function getSessionId(chatbotId: string, isPreview: boolean) {
	const key = getChatbotSessionKey({ chatbotId, isPreview });

	return sessionStorage.getItem(key) ?? undefined;
}

function setSessionId(chatbotId: string, sessionId: string, isPreview: boolean) {
	const key = getChatbotSessionKey({ chatbotId, isPreview });

	sessionStorage.setItem(key, sessionId);
	window.dispatchEvent(
		new StorageEvent("storage", {
			key,
			newValue: sessionId,
			storageArea: sessionStorage,
		}),
	);
}

export function setupChatbotApiInterceptors({
	getChatbotId,
	isPreview = false,
	onAuthError,
}: SetupChatbotApiInterceptorsParams) {
	CHATBOT_API_AXIOS_INSTANCE.interceptors.request.use((requestConfig) => {
		const chatbotId = getChatbotId();
		const sessionId = getSessionId(chatbotId, isPreview);

		if (sessionId) {
			requestConfig.headers[HEADER_SESSION_ID] = sessionId;
		}

		return requestConfig;
	});

	CHATBOT_API_AXIOS_INSTANCE.interceptors.response.use(
		(response) => {
			const sessionId = response.headers[HEADER_SESSION_ID] as string | undefined;

			if (sessionId) {
				const chatbotId = getChatbotId();

				setSessionId(chatbotId, sessionId, isPreview);
			}

			return response;
		},
		async (error: AxiosError) => {
			const originalRequest = error.config as
				| (InternalAxiosRequestConfig & { _retry?: boolean | undefined })
				| undefined;

			if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
				const chatbotId = getChatbotId();
				const { accessToken } = await onAuthError(chatbotId);

				originalRequest.headers.Authorization = `Bearer ${accessToken}`;
				originalRequest._retry = true;

				return CHATBOT_API_AXIOS_INSTANCE(originalRequest);
			}

			throw error;
		},
	);
}

// this const is used in apps/chatbot-api/orval.config.ts
export const chatbotApiAxiosInstance = async <T>(
	config: AxiosRequestConfig,
	options?: AxiosRequestConfig,
): Promise<T> => {
	const { data } = await CHATBOT_API_AXIOS_INSTANCE.request<T>({ ...config, ...options });

	return data;
};

export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;
